import { useEffect, useRef, useSyncExternalStore } from 'react';

// Super-admin material handling matrix (Tag Domain): how classifications / material types are
// shown (normal line, separate weight, hidden), whether they add to net weight and how they are valued.
// Each rule has a normal behaviour and an optional tick box ("Show Dak Wt") with its own behaviour.
export const DEFAULT_MAT_RULES = [
  {
    id: 'dak', name: 'Dak Wt', codes: ['POLKI-DAK', 'POLKI-SLV'], types: [], factor: 0.2, unit: 'ct', color: '#be185d', position: 'top', after: '', style: { bold: true, color: true, italic: false, highlight: true },
    base: { net: true, show: 'hide', price: 'none' },
    option: { on: true, label: 'Show Dak Wt', net: false, show: 'separate', price: 'zero', customers: [] },
  },
  {
    id: 'mounting', name: 'Mountings', codes: ['MOUNTING', 'MOUNTINGS'], types: ['mounting'], factor: 1, unit: 'g', color: '#64748b', position: 'top', after: '', style: { bold: false, color: false, italic: false, highlight: false },
    base: { net: true, show: 'hide', price: 'none' },
    option: { on: false, label: '', net: true, show: 'hide', price: 'none', customers: [] },
  },
];

let rules = DEFAULT_MAT_RULES;
let opts = [];
const subs = new Set();
const emit = () => subs.forEach((f) => f());
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export function setMatRules(list) { rules = Array.isArray(list) ? list : DEFAULT_MAT_RULES; emit(); }
export const getMatRules = () => rules;
export const useMatRules = () => useSyncExternalStore(subscribe, getMatRules);
// Ticked options (rule ids) for whatever is being printed / exported / priced right now.
export function setMatOpts(ids) { const next = [...new Set(ids || [])].sort(); if (next.join() !== opts.join()) { opts = next; emit(); } }
export const getMatOpts = () => opts;
export const useMatOpts = () => useSyncExternalStore(subscribe, getMatOpts);
export const optionRules = () => rules.filter((r) => r.option?.on && r.option.label);
// Rules auto-ticked for a customer (by id or name).
export const customerOpts = (id, name) => optionRules().filter((r) => (r.option.customers || []).some((c) => c === id || (name && c.toLowerCase?.() === name.trim().toLowerCase()))).map((r) => r.id);
// Pieces made for a customer whose options are set (e.g. Show Dak Wt) always get them, ticked or not.
// While a sale / approval is being made, only pieces made for that same buyer are forced.
let buyer = null;
let buyerKey = '';
const getBuyerKey = () => buyerKey;
export function setMatBuyer(b) { const k = b ? `${b.id || ''}|${b.name || ''}` : ''; if (k !== buyerKey) { buyer = b; buyerKey = k; emit(); } }
const lc = (s) => String(s || '').trim().toLowerCase();
export function pieceForcedOpts(det) {
  const id = det?.made_for_id || ''; const name = det?.customer || '';
  if (!id && !name) return [];
  if (buyer && !((buyer.id && buyer.id === id) || (buyer.name && lc(buyer.name) === lc(name)))) return [];
  return customerOpts(id, name);
}
export const tickedFor = (det) => (det?._allOpts ? optionRules().map((r) => r.id) : [...new Set([...opts, ...pieceForcedOpts(det)])]);

const up = (s) => String(s || '').trim().toUpperCase();
const ruleOf = (m) => rules.find((r) => (r.codes || []).some((c) => up(c) && (up(c) === m.code || up(c) === up(m.classification))) || (r.types || []).some((t) => up(t) === up(m.materialType)));

// Split a piece's raw materials by the matrix: normal lines, grams added to net, separate weights.
export function applyMatHandling(materials, ticked = opts) {
  const visible = []; const separate = new Map(); let extraNetGrams = 0;
  for (const m of materials || []) {
    const r = ruleOf(m);
    if (!r) { visible.push(m); continue; }
    const b = r.option?.on && ticked.includes(r.id) ? r.option : r.base;
    const qty = Number(m.carats) || 0;
    if (b.net) extraNetGrams += qty * (Number(r.factor) || 0);
    if (b.show === 'line') visible.push({ ...m, ...(b.price === 'zero' ? { zeroRate: true } : {}), matStyle: styleOf(r) });
    else if (b.show === 'separate') {
      const s = separate.get(r.id) || { key: `sep-${r.id}`, id: r.id, label: r.name, unit: r.unit, color: r.color, style: styleOf(r), price: b.price, carats: 0, qty: 0 };
      s.carats += qty; s.qty += r.unit === 'g' ? qty * (Number(r.factor) || 0) : qty;
      separate.set(r.id, s);
    }
  }
  return { visible, extraNetGrams, separate: [...separate.values()] };
}

// Display style of a rule: bold / own colour / italic / highlighted background.
const DEF_STYLE = { bold: true, color: true, italic: false, highlight: true };
export const styleOf = (r) => ({ ...DEF_STYLE, ...(r?.style || {}), hex: r?.color || '#be185d' });
export const styleOfLabel = (label) => { const r = rules.find((x) => x.name === label) || ruleOf({ code: up(label), classification: label }); return r ? styleOf(r) : null; };
// Light background tint of a colour (mixed with white).
export const tint = (hex, k = 0.88) => { const m = String(hex || '#be185d').replace('#', ''); return [0, 2, 4].map((i) => Math.round(parseInt(m.slice(i, i + 2), 16) * (1 - k) + 255 * k)); };
const h2 = (n) => n.toString(16).padStart(2, '0').toUpperCase();
// ExcelJS cell style for a styled line.
export const xlStyle = (st) => (!st ? {} : { bold: st.bold, color: st.color ? `FF${st.hex.replace('#', '').toUpperCase()}` : undefined, fill: st.highlight ? `FF${tint(st.hex).map(h2).join('')}` : undefined, italic: st.italic });
export const fmtSep = (s) => (s.unit === 'g' ? `${Number(s.qty).toFixed(3)} g` : `${Number(s.qty).toFixed(2)} ct`);
// Separate weights as extra tag rows (shown after the raw-material list).
export const sepRows = (data) => (data?.separate || []).map((s) => ({ ...s, sepRow: true, classification: s.label }));
// Distinct separate-weight columns across tag datas ({label, unit}).
export const sepCols = (list, get = (d) => d.separate) => {
  const m = new Map();
  list.forEach((d) => (get(d) || []).forEach((s) => { if (!m.has(s.label)) m.set(s.label, { label: s.label, unit: s.unit }); }));
  return [...m.values()];
};
export const sepQty = (d, label, get = (x) => x.separate) => (get(d) || []).filter((s) => s.label === label).reduce((t, s) => t + (Number(s.qty) || 0), 0);

// Re-run a builder whenever the ticked options change (data keeps its raw doc in `_raw`).
export function useRebuildOnOpts(setter, rebuild) {
  const key = `${useMatOpts().join()}#${useSyncExternalStore(subscribe, getBuyerKey)}`;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setter((v) => (!v ? v : Array.isArray(v) ? v.map((d) => (d?._raw ? rebuild(d._raw) : d)) : v._raw ? rebuild(v._raw) : v));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Put separate-weight lines into a material list by each rule's position:
// 'top' = right after net weight, 'end' = after all raw materials, 'after' = after the last line matching `after` (e.g. POLKI).
export function placeSep(lines, seps, labelOf = (l) => l.label) {
  const out = [...lines];
  let top = 0;
  seps.forEach((s) => {
    const r = rules.find((x) => x.id === s.id);
    const pos = r?.position || 'top';
    if (pos === 'end') { out.push(s); return; }
    if (pos === 'after' && r.after) {
      const key = up(r.after);
      const re = new RegExp(`(^|[^A-Z0-9])${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
      // Match the classification code or the shown name, from the start of a word (POLKI, D-RB, ROUND).
      let k = -1; out.forEach((l, i) => { if (l !== s && !seps.includes(l) && [l.code, labelOf(l)].some((v) => re.test(up(v)))) k = i; });
      if (k >= 0) { out.splice(k + 1, 0, s); return; }
    }
    out.splice(top, 0, s); top += 1;
  });
  return out;
}
